import { effect } from "@preact/signals";

import type { MainLayoutProps } from "AppRootView";
import type AppRouter from "AppRouter";
import * as iOSBridge from "iOSBridge";
import * as modalController from "modalController";
import {
  NativeActions,
  type NativeMenuItem,
  type NativeMenuItemSpec,
} from "nativeMenuItems";
import * as pageTransitions from "pageTransitions";
import * as languageMap from "shared/languageMap";
import type { StreakData } from "stores/StreakStore";
import stores from "stores/stores";
import * as streakRepair from "streakRepair";
import * as userMenu from "userMenu";

/**
 * The main header (languages, streak, user menu and tabs) as the iOS app
 * draws it natively, in place of the web page's own: the app's top bar and
 * tab bar. Pages without the header hide them.
 */
export interface NativeHeader {
  visible: boolean;
  /** Whether to let the user swipe back to the page before (pageTransitions.ts) */
  canGoBack?: boolean;
  /** Under a full-screen modal: the header dims and the tab bar hides */
  covered?: boolean;
  /** A page opened from another: its back button, in the language menu's place */
  back?: { title: string; action: string };
  /** The language being learned, titling the language menu */
  language?: string;
  languageMenu?: NativeMenuItem[];
  streak?: {
    days: number;
    fractionOfGoalMet: number;
    /** Something for the user to see in its menu, as the web's red dot */
    redDot: boolean;
    menu: NativeMenuItem[];
  };
  user?: { imageURL?: string; menu: NativeMenuItem[] };
  tabs?: { title: string; image: string; selected: boolean; action: string }[];
}

/** Whether the app draws the header: in the iOS app */
export const isEnabled = () => iOSBridge.get() !== undefined;

const actions = new NativeActions();
let current: {
  props: MainLayoutProps | undefined;
  pageName: string;
  appRouter: AppRouter;
};
/** Stops publishing the header as what it shows changes */
let stopPublishing: (() => void) | undefined;

const languageItems = (
  list: { code: string }[],
  selected: string,
  select: (code: string) => void
): NativeMenuItemSpec[] =>
  list.map(({ code }) => ({
    title: languageMap.map[code]?.english ?? code,
    checked: code === selected,
    perform: () => select(code),
  }));

const fromUserMenu = ({
  title,
  image,
  onClick,
}: userMenu.UserMenuItem): NativeMenuItemSpec => ({
  title,
  image,
  perform: onClick,
});

/** The streak, its menu standing in for the web's popover (StreakView.tsx) */
const streakHeader = (
  { days, repairableDays, todayStats }: StreakData,
  showProgress: () => void
): NativeHeader["streak"] => {
  const offer = streakRepair.offer(repairableDays);
  const [notification] =
    stores.storedNotifications.notifications.value.slice(-1);
  return {
    days,
    fractionOfGoalMet: todayStats.fractionOfGoalMet,
    // A menu can't open itself, as the web's popover does for a long lost streak
    redDot:
      Boolean(notification) ||
      (offer !== undefined &&
        repairableDays >= streakRepair.minimumDaysToNudge),
    menu: actions.describe(
      [
        // First, and in its own section, so it stands apart from the streak
        ...(offer
          ? [
              {
                title: "",
                inline: true,
                children: [
                  {
                    // With a non-breaking hyphen, wrapping before "12-day"
                    title: `Repair my ${offer.repairableDays}\u2011day streak`,
                    prominent: true,
                    ...(offer.kind === "repair"
                      ? {
                          subtitle: `${offer.repairsLeft} repair${
                            offer.repairsLeft === 1 ? "" : "s"
                          } left this month`,
                          image: "bandage",
                          perform: () => streakRepair.repair(),
                        }
                      : {
                          subtitle: "Upgrade to Premium",
                          image: "star",
                          perform: () => void streakRepair.showUpsell(),
                        }),
                  },
                ],
              },
            ]
          : []),
        {
          title: `${days}-day streak`,
          subtitle: `${Math.round(
            Math.min(todayStats.fractionOfGoalMet, 1) * 100
          )}% of today's goal`,
          image: "flame",
          perform: showProgress,
        },
        ...(notification
          ? [
              {
                title: notification.body,
                inline: true,
                children: [
                  {
                    title: "Dismiss",
                    image: "xmark",
                    perform: () =>
                      void stores.storedNotifications.deleteAllStoredNotifications(),
                  },
                ],
              },
            ]
          : []),
      ],
      "streak"
    ),
  };
};

const build = (): NativeHeader => {
  const { props, pageName, appRouter } = current;
  const canGoBack =
    !pageTransitions.isTabPage(pageName) && pageTransitions.canGoBack();
  if (!props) {
    return { visible: false, canGoBack };
  }

  const navigate = (path: string) => () =>
    appRouter.navigate(path, { trigger: true });
  const { user } = props;
  const learning = user.currentLearningLanguage;
  const back = pageTransitions.back(pageName);
  const streakData = props.streakStore.data.value;
  // The current language, then the user's others, ahead of all the rest
  const featured = [
    ...new Set([learning, ...(props.activeLanguages ?? [])]),
  ].filter((code) => languageMap.learningMap[code]);

  return {
    visible: true,
    canGoBack,
    covered: modalController.fullScreenShown.value,
    back: back && {
      title: back.title,
      action: actions.add("back", () => back.go(appRouter)),
    },
    language: languageMap.map[learning]?.english ?? learning,
    languageMenu: actions.describe(
      [
        {
          title: "I'm learning",
          inline: true,
          children: [
            ...languageItems(
              featured.map((code) => ({ code })),
              learning,
              props.onLearningLanguageSelect
            ),
            {
              title: "Other languages",
              image: "globe",
              children: languageItems(
                languageMap.learningList,
                learning,
                props.onLearningLanguageSelect
              ),
            },
          ],
        },
        {
          title: "I know",
          subtitle: languageMap.map[user.firstLanguage]?.english,
          image: "character.bubble",
          children: languageItems(
            languageMap.list,
            user.firstLanguage,
            props.onFirstLanguageSelect
          ),
        },
      ],
      "language"
    ),
    streak:
      props.showStreak && streakData
        ? streakHeader(streakData, navigate("/progress"))
        : undefined,
    user: {
      imageURL: user.imageURL,
      menu: actions.describe(
        [
          {
            title: user.displayName,
            inline: true,
            children: userMenu.items(props, { app: true }).map(fromUserMenu),
          },
          fromUserMenu(userMenu.logout(props)),
        ],
        "user"
      ),
    },
    tabs: [
      {
        title: "Home",
        image: "house",
        perform: navigate(pageTransitions.tabPaths.Home),
      },
      {
        title: "Library",
        image: "books.vertical",
        perform: navigate(pageTransitions.tabPaths.Library),
      },
      {
        title: "Discover",
        image: "safari",
        perform: navigate(pageTransitions.tabPaths.Discover),
      },
      {
        title: "Words",
        image: "character.book.closed",
        perform: props.onWordsClick,
      },
      {
        title: "Practice",
        image: "rectangle.on.rectangle.angled",
        perform: props.onPracticeClick,
      },
    ].map(({ title, image, perform }) => ({
      title,
      image,
      selected: pageTransitions.tabOf(pageName) === title,
      action: actions.add(`tab/${title}`, () => {
        pageTransitions.switchTab();
        perform();
      }),
    })),
  };
};

const publish = () => {
  const app = iOSBridge.get();
  if (app) {
    actions.reset();
    app.setHeader(build());
  }
};

/**
 * Describes the header for the page shown, or hides it (no `props`), and
 * keeps the description up to date.
 */
export const show = (
  props: MainLayoutProps | undefined,
  pageName: string,
  appRouter: AppRouter
) => {
  current = { props, pageName, appRouter };
  window.READLANG.nativeHeader = { perform: actions.perform };
  if (props?.showStreak && !stores.streak.hasData) {
    // The web header's streak view would have loaded it. (Only for a header
    // showing it: signed out, the request is unauthorized, reloading the app.)
    void stores.streak.fetch();
  }
  if (stopPublishing) {
    publish();
    return;
  }

  // Publishes now, and again as the streak, its notifications and the user's
  // streak repairs load and change, and full-screen modals open and close
  stopPublishing = effect(() => {
    void stores.streak.data.value;
    void stores.storedNotifications.notifications.value;
    void stores.user.state.value;
    void modalController.fullScreenShown.value;
    publish();
  });
};

export const _functionsForUnitTests = {
  /** As though never shown, so later tests' app bridges aren't published to */
  reset: () => {
    stopPublishing?.();
    stopPublishing = undefined;
  },
};
